import { useState, useRef, useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { 
  Lock, 
  Loader2, 
  DollarSign, 
  ShoppingCart, 
  TrendingUp, 
  TrendingDown,
  Clock, 
  AlertCircle,
  AlertTriangle,
  Package,
  Users,
  Globe,
  Monitor,
  Smartphone,
  Tablet,
  RefreshCw,
  Eye,
  MousePointer,
  Timer,
  Target,
  MapPin,
  ExternalLink,
  Zap,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Lightbulb,
  Percent,
  ShoppingBag,
  LogIn,
  LogOut,
  ChevronRight,
  ChevronDown,
  CheckCircle,
  XCircle,
  Info,
  BarChart3,
  PieChart as PieChartIcon,
  Sparkles,
  Download,
  FileSpreadsheet,
  CreditCard,
  Leaf,
  TrendingUp as TrendUp,
  AlertOctagon,
  CircleDollarSign,
  Banknote,
} from "lucide-react";
import {
  exportFullGAReport,
  exportGATrafficSummary,
  exportGATopPages,
  exportGASources,
  exportGADevices,
  exportGACountries,
  exportGADailyTrends,
  exportGAFunnel,
  exportGALandingPages,
  exportFullStoreReport,
  exportStoreMetrics,
  exportTopProducts,
  exportTopCustomers,
  exportRecentOrders,
} from "@/utils/exportAnalytics";
import { ParticleBackground } from "@/components/ParticleBackground";
import Page from "@/components/layout/Page";
import { SEOHead } from "@/components/seo/SEOHead";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  LineChart,
  Line,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";

// ==================== TYPES ====================

interface StoreMetrics {
  revenue: { today: number; sevenDays: number; thirtyDays: number };
  orders: { today: number; sevenDays: number; thirtyDays: number };
  avgOrderValue: number;
  unfulfilledCount: number;
  avgFulfilmentHours: number;
}

interface StoreChartDataPoint {
  date: string;
  revenue: number;
  orders: number;
}

interface ProductStat {
  name: string;
  units: number;
  revenue: number;
  lastOrdered: string;
}

interface CustomerStat {
  email: string;
  name: string;
  spend: number;
  orders: number;
  lastOrder: string;
}

interface RecentOrder {
  id: string;
  date: string;
  customer: string;
  total: number;
  status: string;
  fulfilled: boolean;
}

interface StoreAnalyticsData {
  metrics: StoreMetrics;
  chartData: StoreChartDataPoint[];
  topProducts: ProductStat[];
  topCustomers: CustomerStat[];
  recentTrends: RecentOrder[];
}

// Enhanced GA4 Types for Owner Cockpit v2
interface GAData {
  range: string;
  summary: {
    users: number;
    sessions: number;
    pageviews: number;
    newUsers: number;
    returningUsers: number;
    newUserRate: number;
    returningUserRate: number;
    engagedSessions: number;
    engagementRate: number;
    avgSessionDuration: number;
    conversions: number;
    revenue: number;
    purchases: number;
    conversionRate: number;
    revenuePerVisitor: number;
    revenuePerSession: number;
    avgOrderValue: number;
  };
  comparison: {
    sessionsChange: number;
    usersChange: number;
    pageviewsChange: number;
    revenueChange: number;
    conversionsChange: number;
    purchasesChange: number;
    conversionRateChange: number;
    aovChange: number;
    rpvChange: number;
    prevSessions: number;
    prevUsers: number;
    prevRevenue: number;
    prevConversions: number;
    prevPurchases: number;
  };
  funnel: {
    sessions: number;
    viewItem: number;
    addToCart: number;
    beginCheckout: number;
    purchase: number;
    addToCartRate: number;
    checkoutStartRate: number;
    checkoutToPurchaseRate: number;
    overallFunnelConversion: number;
    cartAbandonmentRate: number;
    checkoutAbandonmentRate: number;
  };
  userTypes: {
    new: { sessions: number; conversions: number; revenue: number; purchases: number };
    returning: { sessions: number; conversions: number; revenue: number; purchases: number };
  };
  channels: { channel: string; sessions: number; revenue: number; purchases: number; conversions: number; conversionRate: number }[];
  paidVsOrganic: {
    paid: { sessions: number; revenue: number; purchases: number; conversions: number; conversionRate: number };
    organic: { sessions: number; revenue: number; purchases: number; conversions: number; conversionRate: number };
  };
  topPages: { pageTitle: string; pagePath: string; pageViews: number }[];
  landingPages: { landingPage: string; sessions: number; conversions: number; revenue: number; purchases: number }[];
  exitPages: { pagePath: string; sessions: number }[];
  topSources: { sourceMedium: string; sessions: number }[];
  sourceRevenue: { sourceMedium: string; sessions: number; conversions: number; revenue: number; purchases: number }[];
  deviceBreakdown: { deviceCategory: string; sessions: number }[];
  deviceRevenue: { deviceCategory: string; sessions: number; revenue: number; purchases: number }[];
  countryBreakdown: { country: string; sessions: number }[];
  countryRevenue: { country: string; sessions: number; revenue: number; purchases: number }[];
  cityBreakdown: { city: string; sessions: number }[];
  dailyTrend: { date: string; users: number; sessions: number; pageviews: number; revenue: number; purchases: number; conversions: number; conversionRate: number; revenuePerVisitor: number }[];
  realtime: {
    activeUsers: number;
    topPages: { page: string; activeUsers: number }[];
    topSources: { source: string; activeUsers: number }[];
    devices: { device: string; activeUsers: number }[];
  };
  alerts: { type: string; severity: string; title: string; message: string; metric: string; recommendation: string }[];
  intelligence: {
    summary: string;
    whatsHappening: string;
    whatToDoNext: string;
    biggestRisk: string;
    opportunity: string;
  };
}

// ==================== UTILITIES ====================

const formatCurrency = (value: number) => `£${value.toFixed(2)}`;
const formatCurrencyShort = (value: number) => {
  if (value >= 1000) return `£${(value / 1000).toFixed(1)}K`;
  return `£${value.toFixed(0)}`;
};

const formatUKDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { 
    day: '2-digit', 
    month: 'short',
    year: 'numeric'
  });
};

const formatShortDate = (dateString: string) => {
  if (dateString.length === 8 && !dateString.includes('-')) {
    const year = dateString.slice(0, 4);
    const month = dateString.slice(4, 6);
    const day = dateString.slice(6, 8);
    dateString = `${year}-${month}-${day}`;
  }
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', { 
    day: '2-digit', 
    month: 'short'
  });
};

const formatHours = (hours: number) => {
  if (hours < 24) return `${hours.toFixed(1)}h`;
  const days = hours / 24;
  return `${days.toFixed(1)}d`;
};

const formatDuration = (seconds: number) => {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${minutes}m ${secs}s`;
};

const formatNumber = (num: number) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const formatPercent = (num: number) => `${num.toFixed(1)}%`;

const DEVICE_COLORS = {
  desktop: "#10b981",
  mobile: "#06b6d4",
  tablet: "#8b5cf6",
};

const CHART_COLORS = ["#10b981", "#06b6d4", "#8b5cf6", "#f59e0b", "#ef4444", "#ec4899"];
const FUNNEL_COLORS = ["#06b6d4", "#8b5cf6", "#f59e0b", "#10b981"];
const CHANNEL_COLORS: Record<string, string> = {
  "Direct": "#10b981",
  "Organic": "#06b6d4",
  "Paid": "#f59e0b",
  "Social": "#ec4899",
  "Referral": "#8b5cf6",
  "Email": "#ef4444",
  "Other": "#6b7280",
};

// ==================== MAIN COMPONENT ====================

export default function Analytics() {
  const isPublishedSite = !window.location.hostname.includes('lovableproject.com') && 
                          !window.location.hostname.includes('localhost') &&
                          !window.location.hostname.includes('samboards.co.uk');
  
  const { session, isLoading: authLoading } = useAuth();
  
  const [isPasswordAuth, setIsPasswordAuth] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  
  const [storeData, setStoreData] = useState<StoreAnalyticsData | null>(null);
  const [gaData, setGAData] = useState<GAData | null>(null);
  const [isLoadingStore, setIsLoadingStore] = useState(false);
  const [isLoadingGA, setIsLoadingGA] = useState(false);
  const [gaError, setGAError] = useState("");
  const [gaRange, setGARange] = useState<"7d" | "30d" | "90d">("30d");
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  
  const sessionPasswordRef = useRef<string>("");

  if (isPublishedSite) {
    console.warn('🚫 Analytics dashboard blocked on unauthorized site');
    return <Navigate to="/404" replace />;
  }

  const isAuthenticated = !!session || isPasswordAuth;

  useEffect(() => {
    if (isAuthenticated && session) {
      fetchGAData();
    }
  }, [isAuthenticated, session, gaRange]);

  useEffect(() => {
    if (isPasswordAuth && storeData) {
      fetchGAData();
    }
  }, [isPasswordAuth, gaRange]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => {
      fetchGAData();
    }, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const fetchGAData = async () => {
    if (!session && !isPasswordAuth) return;
    
    setIsLoadingGA(true);
    setGAError("");
    
    try {
      const { data, error } = await supabase.functions.invoke('ga-analytics', {
        body: {},
      });
      
      if (error) {
        console.error("GA fetch error:", error);
        setGAError("Failed to fetch website traffic data");
        return;
      }
      
      if (data?.error) {
        setGAError(data.error);
        return;
      }
      
      setGAData(data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("GA Analytics error:", err);
      setGAError("Failed to load website analytics");
    } finally {
      setIsLoadingGA(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setAuthError("");

    try {
      const { data, error } = await supabase.functions.invoke('get-analytics', {
        body: { password }
      });

      if (error) {
        if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          setAuthError("Invalid password");
        } else {
          setAuthError("Authentication failed. Please try again.");
        }
        return;
      }

      sessionPasswordRef.current = password;
      setIsPasswordAuth(true);
      setStoreData(data);
    } catch (err) {
      console.error("Auth error:", err);
      setAuthError("Authentication failed. Please try again.");
    } finally {
      setIsAuthenticating(false);
    }
  };

  const refreshStoreData = async () => {
    setIsLoadingStore(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-analytics', {
        body: { password: sessionPasswordRef.current }
      });
      if (!error && data) {
        setStoreData(data);
      }
    } catch (error) {
      console.error("Error refreshing store analytics:", error);
    } finally {
      setIsLoadingStore(false);
    }
  };

  const refreshAll = () => {
    if (isPasswordAuth) {
      refreshStoreData();
    }
    fetchGAData();
  };

  // Funnel data for chart
  const funnelData = useMemo(() => {
    if (!gaData?.funnel) return [];
    const { sessions, viewItem, addToCart, beginCheckout, purchase } = gaData.funnel;
    return [
      { name: "Sessions", value: sessions, fill: FUNNEL_COLORS[0] },
      { name: "Add to Cart", value: addToCart, fill: FUNNEL_COLORS[1] },
      { name: "Checkout", value: beginCheckout, fill: FUNNEL_COLORS[2] },
      { name: "Purchase", value: purchase, fill: FUNNEL_COLORS[3] },
    ].filter(item => item.value > 0);
  }, [gaData]);

  // Alerts to display
  const displayAlerts = useMemo(() => {
    if (!gaData?.alerts) return [];
    return showAllAlerts ? gaData.alerts : gaData.alerts.slice(0, 5);
  }, [gaData, showAllAlerts]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <SEOHead
          title="Owner Cockpit"
          description="View store analytics and performance metrics"
          canonical="/analytics"
          noindex={true}
        />

        <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50 flex items-center justify-center">
          <ParticleBackground />
          
          <Card className="w-full max-w-md mx-4 bg-slate-900/80 border-slate-700/60 backdrop-blur-xl">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-emerald-400" />
              </div>
              <CardTitle className="text-xl text-slate-100">Owner Cockpit</CardTitle>
              <CardDescription className="text-slate-400">
                Enter your password to access analytics
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-800/50 border-slate-600 text-slate-100"
                  autoFocus
                />
                
                {authError && (
                  <Alert variant="destructive" className="bg-red-900/30 border-red-700">
                    <AlertDescription>{authError}</AlertDescription>
                  </Alert>
                )}
                
                <Button 
                  type="submit" 
                  className="w-full bg-emerald-600 hover:bg-emerald-500"
                  disabled={isAuthenticating || !password}
                >
                  {isAuthenticating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Authenticating...
                    </>
                  ) : (
                    "Access Cockpit"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const storeMetrics = storeData?.metrics;
  const storeChartData = storeData?.chartData || [];
  const topProducts = storeData?.topProducts || [];
  const topCustomers = storeData?.topCustomers || [];
  const recentTrends = storeData?.recentTrends || [];

  return (
    <>
      <SEOHead
        title="Owner Cockpit | Executive Dashboard"
        description="Complete analytics dashboard for store and website"
        canonical="/analytics"
        noindex={true}
      />

      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground />

        <Page>
          <section className="space-y-6 pb-24">
            {/* Header */}
            <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl md:text-[40px] leading-tight font-semibold page-title-gradient mb-2 flex items-center gap-3">
                  <Sparkles className="w-8 h-8 text-emerald-400" />
                  Owner Cockpit
                </h1>
                <p className="text-sm md:text-base text-slate-300/85">
                  Executive intelligence for your business
                </p>
              </div>
              <div className="flex items-center gap-3">
                {lastRefresh && (
                  <span className="text-xs text-slate-500">
                    Updated {lastRefresh.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
                <Button 
                  onClick={refreshAll} 
                  disabled={isLoadingStore || isLoadingGA}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  {(isLoadingStore || isLoadingGA) ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </>
                  )}
                </Button>
              </div>
            </header>

            {/* ==================== EXECUTIVE STRIP - TOP KPIs ==================== */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              <ExecutiveCard
                title="Revenue"
                value={formatCurrencyShort(gaData?.summary?.revenue || 0)}
                change={gaData?.comparison?.revenueChange}
                icon={<DollarSign className="w-4 h-4" />}
                loading={isLoadingGA}
                color="emerald"
              />
              <ExecutiveCard
                title="Purchases"
                value={formatNumber(gaData?.summary?.purchases || 0)}
                change={gaData?.comparison?.purchasesChange}
                icon={<ShoppingBag className="w-4 h-4" />}
                loading={isLoadingGA}
                color="cyan"
              />
              <ExecutiveCard
                title="Conversion"
                value={`${gaData?.summary?.conversionRate || 0}%`}
                change={gaData?.comparison?.conversionRateChange}
                icon={<Target className="w-4 h-4" />}
                loading={isLoadingGA}
                color="violet"
              />
              <ExecutiveCard
                title="AOV"
                value={formatCurrencyShort(gaData?.summary?.avgOrderValue || 0)}
                change={gaData?.comparison?.aovChange}
                icon={<Banknote className="w-4 h-4" />}
                loading={isLoadingGA}
                color="amber"
              />
              <ExecutiveCard
                title="Rev/Visitor"
                value={formatCurrencyShort(gaData?.summary?.revenuePerVisitor || 0)}
                change={gaData?.comparison?.rpvChange}
                icon={<CircleDollarSign className="w-4 h-4" />}
                loading={isLoadingGA}
                color="emerald"
              />
              <ExecutiveCard
                title="Users"
                value={formatNumber(gaData?.summary?.users || 0)}
                change={gaData?.comparison?.usersChange}
                icon={<Users className="w-4 h-4" />}
                loading={isLoadingGA}
                color="cyan"
              />
              <ExecutiveCard
                title="Cart Abandon"
                value={`${gaData?.funnel?.cartAbandonmentRate || 0}%`}
                icon={<ShoppingCart className="w-4 h-4" />}
                loading={isLoadingGA}
                color="rose"
                invertColor
              />
              <ExecutiveCard
                title="Checkout Drop"
                value={`${gaData?.funnel?.checkoutAbandonmentRate || 0}%`}
                icon={<XCircle className="w-4 h-4" />}
                loading={isLoadingGA}
                color="rose"
                invertColor
              />
            </div>

            {/* ==================== ALERTS SECTION ==================== */}
            {gaData?.alerts && gaData.alerts.length > 0 && (
              <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertOctagon className="w-5 h-5 text-amber-400" />
                      <CardTitle className="text-slate-200 text-base font-medium">Alerts & Opportunities</CardTitle>
                      <Badge variant="outline" className="text-xs border-slate-600 text-slate-400">
                        {gaData.alerts.length}
                      </Badge>
                    </div>
                    {gaData.alerts.length > 5 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAllAlerts(!showAllAlerts)}
                        className="text-slate-400 hover:text-slate-200"
                      >
                        {showAllAlerts ? "Show less" : "Show all"}
                        <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showAllAlerts ? 'rotate-180' : ''}`} />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {displayAlerts.map((alert, index) => (
                      <div
                        key={index}
                        className={`flex items-start gap-3 p-3 rounded-lg border ${
                          alert.severity === 'critical'
                            ? 'bg-red-950/30 border-red-800/40'
                            : alert.severity === 'warn'
                            ? 'bg-amber-950/30 border-amber-800/40'
                            : 'bg-cyan-950/30 border-cyan-800/40'
                        }`}
                      >
                        {alert.severity === 'critical' ? (
                          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                        ) : alert.severity === 'warn' ? (
                          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <Info className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-sm font-medium ${
                              alert.severity === 'critical' ? 'text-red-300' :
                              alert.severity === 'warn' ? 'text-amber-300' : 'text-cyan-300'
                            }`}>
                              {alert.title}
                            </span>
                            <Badge 
                              variant="outline" 
                              className={`text-[10px] px-1.5 py-0 ${
                                alert.severity === 'critical' ? 'border-red-700 text-red-400' :
                                alert.severity === 'warn' ? 'border-amber-700 text-amber-400' : 'border-cyan-700 text-cyan-400'
                              }`}
                            >
                              {alert.severity}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-300">{alert.message}</p>
                          <p className="text-xs text-slate-500 mt-1">→ {alert.recommendation}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ==================== OPERATOR INTELLIGENCE ==================== */}
            {gaData?.intelligence && (
              <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-violet-400" />
                    <CardTitle className="text-slate-200 text-base font-medium">Operator Intelligence</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* What's Happening */}
                  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Activity className="w-4 h-4 text-cyan-400" />
                      <p className="text-xs text-cyan-400 uppercase tracking-wide font-medium">What's Happening</p>
                    </div>
                    <p className="text-slate-200 text-sm leading-relaxed">
                      {gaData.intelligence.whatsHappening || "Metrics stable this period."}
                    </p>
                  </div>

                  {/* What To Do Next */}
                  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-4 h-4 text-emerald-400" />
                      <p className="text-xs text-emerald-400 uppercase tracking-wide font-medium">What To Do Next</p>
                    </div>
                    <p className="text-slate-200 text-sm leading-relaxed">
                      {gaData.intelligence.whatToDoNext || "Continue current strategy."}
                    </p>
                  </div>

                  {/* Biggest Risk */}
                  <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700/50">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                      <p className="text-xs text-amber-400 uppercase tracking-wide font-medium">Biggest Risk</p>
                    </div>
                    <p className="text-slate-200 text-sm leading-relaxed">
                      {gaData.intelligence.biggestRisk || "No critical risks detected."}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* ==================== REALTIME SNAPSHOT ==================== */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400 uppercase tracking-wide">Active Now</span>
                    <span className={`w-2.5 h-2.5 rounded-full ${
                      (gaData?.realtime?.activeUsers || 0) > 10 ? 'bg-emerald-400' :
                      (gaData?.realtime?.activeUsers || 0) > 0 ? 'bg-amber-400' : 'bg-slate-600'
                    } animate-pulse`} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-emerald-400" />
                    <span className="text-2xl font-bold text-slate-100">
                      {gaData?.realtime?.activeUsers || 0}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Last 30 minutes</p>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400 uppercase tracking-wide">Top Page Now</span>
                    <Eye className="w-4 h-4 text-cyan-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-100 truncate">
                    {gaData?.realtime?.topPages?.[0]?.page || '/'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {gaData?.realtime?.topPages?.[0]?.activeUsers || 0} viewing
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400 uppercase tracking-wide">Top Source Now</span>
                    <ExternalLink className="w-4 h-4 text-violet-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-100 truncate">
                    {gaData?.realtime?.topSources?.[0]?.source || 'Direct'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {gaData?.realtime?.topSources?.[0]?.activeUsers || 0} users
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400 uppercase tracking-wide">Period Revenue</span>
                    <DollarSign className="w-4 h-4 text-emerald-400" />
                  </div>
                  <p className="text-2xl font-bold text-slate-100">
                    {formatCurrencyShort(gaData?.summary?.revenue || 0)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    {gaData?.summary?.purchases || 0} purchases
                  </p>
                </CardContent>
              </Card>
            </div>

            <Tabs defaultValue="overview" className="space-y-6">
              <TabsList className="bg-slate-800/60 border border-slate-700/50">
                <TabsTrigger value="overview" className="data-[state=active]:bg-emerald-600/30 data-[state=active]:text-emerald-300">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="traffic" className="data-[state=active]:bg-cyan-600/30 data-[state=active]:text-cyan-300">
                  <Globe className="w-4 h-4 mr-2" />
                  Traffic
                </TabsTrigger>
                <TabsTrigger value="conversions" className="data-[state=active]:bg-violet-600/30 data-[state=active]:text-violet-300">
                  <Target className="w-4 h-4 mr-2" />
                  Conversions
                </TabsTrigger>
                <TabsTrigger value="store" className="data-[state=active]:bg-amber-600/30 data-[state=active]:text-amber-300">
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Store
                </TabsTrigger>
              </TabsList>

              {/* ==================== OVERVIEW TAB ==================== */}
              <TabsContent value="overview" className="space-y-6">
                {/* Date Range Selector */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400">Period:</span>
                    <div className="flex gap-1">
                      {(["7d", "30d", "90d"] as const).map((range) => (
                        <Button
                          key={range}
                          size="sm"
                          variant={gaRange === range ? "default" : "outline"}
                          className={gaRange === range 
                            ? "bg-emerald-600 hover:bg-emerald-500 text-white" 
                            : "border-slate-600 text-slate-400 hover:bg-slate-800"}
                          onClick={() => setGARange(range)}
                        >
                          {range === "7d" ? "7 Days" : range === "30d" ? "30 Days" : "90 Days"}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  {gaData && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-600/50 text-emerald-400 hover:bg-emerald-900/30"
                      onClick={() => exportFullGAReport({
                        summary: gaData.summary,
                        topPages: gaData.topPages,
                        sourceRevenue: gaData.sourceRevenue,
                        deviceBreakdown: gaData.deviceBreakdown,
                        countryBreakdown: gaData.countryBreakdown,
                        dailyTrend: gaData.dailyTrend,
                        landingPages: gaData.landingPages,
                        funnel: gaData.funnel,
                      }, gaRange)}
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Export Full Report
                    </Button>
                  )}
                </div>

                {gaError && (
                  <Alert variant="destructive" className="bg-red-900/30 border-red-700">
                    <AlertCircle className="w-4 h-4" />
                    <AlertDescription>{gaError}</AlertDescription>
                  </Alert>
                )}

                {/* Funnel Visualization */}
                <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-violet-400" />
                        <CardTitle className="text-violet-300 text-lg">Sales Funnel</CardTitle>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-400">
                        <span>Overall: <span className="text-emerald-400 font-medium">{gaData?.funnel?.overallFunnelConversion || 0}%</span></span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingGA ? (
                      <Skeleton className="h-32 w-full bg-slate-800" />
                    ) : funnelData.length > 0 ? (
                      <div className="space-y-3">
                        {funnelData.map((stage, index) => {
                          const maxValue = funnelData[0]?.value || 1;
                          const width = (stage.value / maxValue) * 100;
                          const dropOff = index > 0 
                            ? Math.round((1 - stage.value / funnelData[index - 1].value) * 100) 
                            : 0;
                          
                          return (
                            <div key={stage.name} className="relative">
                              <div className="flex items-center justify-between text-sm mb-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-300 font-medium">{stage.name}</span>
                                  {dropOff > 0 && (
                                    <Badge variant="outline" className="text-xs border-red-700/50 text-red-400 py-0">
                                      -{dropOff}% drop
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-slate-100 font-bold">{formatNumber(stage.value)}</span>
                              </div>
                              <div className="h-8 bg-slate-800 rounded-lg overflow-hidden">
                                <div 
                                  className="h-full rounded-lg transition-all duration-700 flex items-center justify-end pr-3"
                                  style={{ width: `${Math.max(width, 8)}%`, backgroundColor: stage.fill }}
                                >
                                  {width > 15 && (
                                    <span className="text-white/90 text-xs font-medium">
                                      {formatPercent(width)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-slate-400 text-sm">No funnel data available</p>
                        <p className="text-slate-500 text-xs mt-1">Ensure GA4 e-commerce events are configured</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Paid vs Organic Block */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-amber-400" />
                        <CardTitle className="text-amber-300 text-lg">Paid vs Organic</CardTitle>
                      </div>
                      <CardDescription className="text-slate-400 text-xs">
                        Paid is only worth it if conversion + revenue rises proportionally
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {isLoadingGA ? (
                        <Skeleton className="h-40 w-full bg-slate-800" />
                      ) : (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="p-4 bg-amber-900/20 border border-amber-700/30 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                              <CreditCard className="w-4 h-4 text-amber-400" />
                              <p className="text-amber-300 text-sm font-medium">Paid Traffic</p>
                            </div>
                            <p className="text-2xl font-bold text-slate-100 mb-1">
                              {formatCurrencyShort(gaData?.paidVsOrganic?.paid?.revenue || 0)}
                            </p>
                            <div className="space-y-1 text-xs text-slate-400">
                              <p>{formatNumber(gaData?.paidVsOrganic?.paid?.sessions || 0)} sessions</p>
                              <p>{gaData?.paidVsOrganic?.paid?.purchases || 0} purchases</p>
                              <p className="text-amber-300">{(gaData?.paidVsOrganic?.paid?.conversionRate || 0).toFixed(2)}% conv</p>
                            </div>
                          </div>
                          <div className="p-4 bg-emerald-900/20 border border-emerald-700/30 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                              <Leaf className="w-4 h-4 text-emerald-400" />
                              <p className="text-emerald-300 text-sm font-medium">Organic Traffic</p>
                            </div>
                            <p className="text-2xl font-bold text-slate-100 mb-1">
                              {formatCurrencyShort(gaData?.paidVsOrganic?.organic?.revenue || 0)}
                            </p>
                            <div className="space-y-1 text-xs text-slate-400">
                              <p>{formatNumber(gaData?.paidVsOrganic?.organic?.sessions || 0)} sessions</p>
                              <p>{gaData?.paidVsOrganic?.organic?.purchases || 0} purchases</p>
                              <p className="text-emerald-300">{(gaData?.paidVsOrganic?.organic?.conversionRate || 0).toFixed(2)}% conv</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Channel Breakdown */}
                  <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <PieChartIcon className="w-5 h-5 text-cyan-400" />
                        <CardTitle className="text-cyan-300 text-lg">Revenue by Channel</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {isLoadingGA ? (
                        <Skeleton className="h-40 w-full bg-slate-800" />
                      ) : (gaData?.channels?.length || 0) > 0 ? (
                        <div className="space-y-2">
                          {gaData?.channels.slice(0, 6).map((channel) => {
                            const totalRev = gaData.channels.reduce((sum, c) => sum + c.revenue, 0);
                            const percentage = totalRev > 0 ? (channel.revenue / totalRev) * 100 : 0;
                            const color = CHANNEL_COLORS[channel.channel] || "#6b7280";
                            
                            return (
                              <div key={channel.channel} className="space-y-1">
                                <div className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                                    <span className="text-slate-300">{channel.channel}</span>
                                  </div>
                                  <div className="flex items-center gap-3 text-xs">
                                    <span className="text-slate-400">{formatNumber(channel.sessions)} sess</span>
                                    <span className="text-emerald-400 font-medium">{formatCurrencyShort(channel.revenue)}</span>
                                  </div>
                                </div>
                                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full rounded-full transition-all duration-500"
                                    style={{ width: `${Math.max(percentage, 2)}%`, backgroundColor: color }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center py-6 text-sm">No channel data</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Revenue Trend */}
                <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                  <CardHeader>
                    <CardTitle className="text-emerald-300 text-lg">Revenue & Conversion Trend</CardTitle>
                    <CardDescription className="text-slate-400">Daily performance over selected period</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoadingGA ? (
                      <Skeleton className="h-72 w-full bg-slate-800" />
                    ) : (gaData?.dailyTrend?.length || 0) > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={gaData?.dailyTrend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis 
                            dataKey="date" 
                            stroke="#cbd5e1" 
                            tick={{ fontSize: 10 }}
                            tickFormatter={formatShortDate}
                            interval="preserveStartEnd"
                          />
                          <YAxis yAxisId="left" stroke="#10b981" tick={{ fontSize: 10 }} tickFormatter={(v) => `£${v}`} />
                          <YAxis yAxisId="right" orientation="right" stroke="#8b5cf6" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#1e293b",
                              border: "1px solid #334155",
                              borderRadius: "8px",
                              color: "#f1f5f9",
                            }}
                            labelFormatter={(label) => formatShortDate(label)}
                            formatter={(value: number, name: string) => {
                              if (name === 'revenue') return [`£${value.toFixed(2)}`, 'Revenue'];
                              if (name === 'conversionRate') return [`${value.toFixed(2)}%`, 'Conv Rate'];
                              if (name === 'purchases') return [value, 'Purchases'];
                              return [value, name];
                            }}
                          />
                          <Legend />
                          <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} dot={false} name="Revenue" />
                          <Line yAxisId="right" type="monotone" dataKey="conversionRate" stroke="#8b5cf6" strokeWidth={2} dot={false} name="Conv Rate" />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-slate-400 text-center py-12">No trend data available</p>
                    )}
                  </CardContent>
                </Card>

                {/* Revenue Attribution Table */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <LogIn className="w-5 h-5 text-cyan-400" />
                        <CardTitle className="text-cyan-300 text-lg">Top Landing Pages by Revenue</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {isLoadingGA ? (
                        <div className="space-y-2">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Skeleton key={i} className="h-12 w-full bg-slate-800" />
                          ))}
                        </div>
                      ) : (gaData?.landingPages?.length || 0) > 0 ? (
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                          {gaData?.landingPages.slice(0, 8).map((page, index) => (
                            <div
                              key={page.landingPage}
                              className="flex items-center justify-between p-2.5 bg-slate-800/50 rounded-lg text-sm"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-cyan-400 font-semibold w-5">#{index + 1}</span>
                                <div className="min-w-0 flex-1">
                                  <p className="text-slate-200 truncate text-xs">{page.landingPage}</p>
                                  <p className="text-slate-500 text-xs">
                                    {formatNumber(page.sessions)} sess • {page.purchases} purch
                                  </p>
                                </div>
                              </div>
                              <span className="text-emerald-300 font-bold text-sm ml-2">
                                {formatCurrency(page.revenue)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center py-6 text-sm">No landing page data</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <ExternalLink className="w-5 h-5 text-violet-400" />
                        <CardTitle className="text-violet-300 text-lg">Top Sources by Revenue</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {isLoadingGA ? (
                        <div className="space-y-2">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Skeleton key={i} className="h-12 w-full bg-slate-800" />
                          ))}
                        </div>
                      ) : (gaData?.sourceRevenue?.length || 0) > 0 ? (
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                          {gaData?.sourceRevenue.slice(0, 8).map((source, index) => (
                            <div
                              key={source.sourceMedium}
                              className="flex items-center justify-between p-2.5 bg-slate-800/50 rounded-lg text-sm"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-violet-400 font-semibold w-5">#{index + 1}</span>
                                <div className="min-w-0 flex-1">
                                  <p className="text-slate-200 truncate text-xs">{source.sourceMedium}</p>
                                  <p className="text-slate-500 text-xs">
                                    {formatNumber(source.sessions)} sess • {source.purchases} purch
                                  </p>
                                </div>
                              </div>
                              <span className="text-emerald-300 font-bold text-sm ml-2">
                                {formatCurrency(source.revenue)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center py-6 text-sm">No source revenue data</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* ==================== TRAFFIC TAB ==================== */}
              <TabsContent value="traffic" className="space-y-6">
                {/* KPI Summary */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  <MetricCardWithChange
                    title="Sessions"
                    icon={<MousePointer className="w-4 h-4 text-cyan-400" />}
                    loading={isLoadingGA}
                    value={formatNumber(gaData?.summary?.sessions || 0)}
                    change={gaData?.comparison?.sessionsChange}
                  />
                  <MetricCardWithChange
                    title="Users"
                    icon={<Users className="w-4 h-4 text-emerald-400" />}
                    loading={isLoadingGA}
                    value={formatNumber(gaData?.summary?.users || 0)}
                    change={gaData?.comparison?.usersChange}
                  />
                  <MetricCard
                    title="New Users"
                    icon={<LogIn className="w-4 h-4 text-cyan-400" />}
                    loading={isLoadingGA}
                    value={`${formatNumber(gaData?.summary?.newUsers || 0)} (${gaData?.summary?.newUserRate || 0}%)`}
                  />
                  <MetricCard
                    title="Returning"
                    icon={<RefreshCw className="w-4 h-4 text-violet-400" />}
                    loading={isLoadingGA}
                    value={`${formatNumber(gaData?.summary?.returningUsers || 0)} (${gaData?.summary?.returningUserRate || 0}%)`}
                  />
                  <MetricCard
                    title="Engagement"
                    icon={<Target className="w-4 h-4 text-amber-400" />}
                    loading={isLoadingGA}
                    value={`${((gaData?.summary?.engagementRate || 0) * 100).toFixed(1)}%`}
                  />
                  <MetricCard
                    title="Avg Session"
                    icon={<Timer className="w-4 h-4 text-rose-400" />}
                    loading={isLoadingGA}
                    value={formatDuration(gaData?.summary?.avgSessionDuration || 0)}
                  />
                </div>

                {/* Traffic Trend */}
                <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                  <CardHeader>
                    <CardTitle className="text-cyan-300 text-lg">Traffic Trend</CardTitle>
                    <CardDescription className="text-slate-400">Users & Sessions over time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoadingGA ? (
                      <Skeleton className="h-64 w-full bg-slate-800" />
                    ) : (gaData?.dailyTrend?.length || 0) > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={gaData?.dailyTrend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis 
                            dataKey="date" 
                            stroke="#cbd5e1" 
                            tick={{ fontSize: 10 }}
                            tickFormatter={formatShortDate}
                            interval="preserveStartEnd"
                          />
                          <YAxis stroke="#cbd5e1" tick={{ fontSize: 10 }} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "#1e293b",
                              border: "1px solid #334155",
                              borderRadius: "8px",
                              color: "#f1f5f9",
                            }}
                            labelFormatter={(label) => formatShortDate(label)}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="sessions" stroke="#06b6d4" strokeWidth={2} dot={false} name="Sessions" />
                          <Line type="monotone" dataKey="users" stroke="#10b981" strokeWidth={2} dot={false} name="Users" />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-slate-400 text-center py-12">No trend data available</p>
                    )}
                  </CardContent>
                </Card>

                {/* Sources, Devices, Countries */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <ExternalLink className="w-5 h-5 text-violet-400" />
                        <CardTitle className="text-violet-300 text-lg">Traffic Sources</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {isLoadingGA ? (
                        <div className="space-y-2">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Skeleton key={i} className="h-10 w-full bg-slate-800" />
                          ))}
                        </div>
                      ) : (gaData?.topSources?.length || 0) > 0 ? (
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                          {gaData?.topSources.map((source, index) => (
                            <div
                              key={source.sourceMedium}
                              className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg text-sm"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-violet-400 font-semibold w-4">#{index + 1}</span>
                                <span className="text-slate-200 truncate text-xs">{source.sourceMedium}</span>
                              </div>
                              <span className="text-slate-100 font-bold text-xs ml-2">{formatNumber(source.sessions)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center py-6 text-sm">No source data</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <Monitor className="w-5 h-5 text-emerald-400" />
                        <CardTitle className="text-emerald-300 text-lg">Devices</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {isLoadingGA ? (
                        <Skeleton className="h-48 w-full bg-slate-800" />
                      ) : (gaData?.deviceBreakdown?.length || 0) > 0 ? (
                        <div className="space-y-3">
                          {gaData?.deviceBreakdown.map((device) => {
                            const total = gaData.deviceBreakdown.reduce((sum, d) => sum + d.sessions, 0);
                            const percentage = total > 0 ? (device.sessions / total) * 100 : 0;
                            const Icon = device.deviceCategory === 'desktop' ? Monitor 
                              : device.deviceCategory === 'mobile' ? Smartphone 
                              : Tablet;
                            const color = DEVICE_COLORS[device.deviceCategory as keyof typeof DEVICE_COLORS] || "#6b7280";
                            
                            return (
                              <div key={device.deviceCategory} className="space-y-1">
                                <div className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    <Icon className="w-4 h-4" style={{ color }} />
                                    <span className="text-slate-300 capitalize">{device.deviceCategory}</span>
                                  </div>
                                  <span className="text-slate-400">{percentage.toFixed(1)}%</span>
                                </div>
                                <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full rounded-full transition-all duration-500"
                                    style={{ width: `${percentage}%`, backgroundColor: color }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center py-6 text-sm">No device data</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-amber-400" />
                        <CardTitle className="text-amber-300 text-lg">Countries</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {isLoadingGA ? (
                        <div className="space-y-2">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Skeleton key={i} className="h-10 w-full bg-slate-800" />
                          ))}
                        </div>
                      ) : (gaData?.countryBreakdown?.length || 0) > 0 ? (
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                          {gaData?.countryBreakdown.map((country, index) => (
                            <div
                              key={country.country}
                              className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg text-sm"
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <span className="text-amber-400 font-semibold w-4">#{index + 1}</span>
                                <span className="text-slate-200 truncate text-xs">{country.country}</span>
                              </div>
                              <span className="text-slate-100 font-bold text-xs ml-2">{formatNumber(country.sessions)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center py-6 text-sm">No country data</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Top Pages */}
                <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Eye className="w-5 h-5 text-cyan-400" />
                      <CardTitle className="text-cyan-300 text-lg">Top Pages</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingGA ? (
                      <div className="space-y-2">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Skeleton key={i} className="h-12 w-full bg-slate-800" />
                        ))}
                      </div>
                    ) : (gaData?.topPages?.length || 0) > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {gaData?.topPages.map((page, index) => (
                          <div
                            key={page.pagePath}
                            className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <span className="text-cyan-400 font-semibold text-sm w-5">#{index + 1}</span>
                              <div className="min-w-0 flex-1">
                                <p className="text-slate-200 text-sm truncate">{page.pageTitle}</p>
                                <p className="text-slate-500 text-xs truncate">{page.pagePath}</p>
                              </div>
                            </div>
                            <span className="text-slate-100 font-bold text-sm ml-2">{formatNumber(page.pageViews)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-400 text-center py-8">No page data</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ==================== CONVERSIONS TAB ==================== */}
              <TabsContent value="conversions" className="space-y-6">
                {/* Conversion KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <MetricCardWithChange
                    title="Conversion Rate"
                    icon={<Percent className="w-4 h-4 text-emerald-400" />}
                    loading={isLoadingGA}
                    value={`${gaData?.summary?.conversionRate || 0}%`}
                    change={gaData?.comparison?.conversionRateChange}
                  />
                  <MetricCard
                    title="Add to Cart"
                    icon={<ShoppingBag className="w-4 h-4 text-violet-400" />}
                    loading={isLoadingGA}
                    value={`${gaData?.funnel?.addToCartRate || 0}%`}
                    subtitle={`${gaData?.funnel?.addToCart || 0} events`}
                  />
                  <MetricCard
                    title="Cart → Checkout"
                    icon={<ShoppingCart className="w-4 h-4 text-cyan-400" />}
                    loading={isLoadingGA}
                    value={`${gaData?.funnel?.checkoutStartRate || 0}%`}
                  />
                  <MetricCard
                    title="Checkout → Purchase"
                    icon={<CheckCircle className="w-4 h-4 text-emerald-400" />}
                    loading={isLoadingGA}
                    value={`${gaData?.funnel?.checkoutToPurchaseRate || 0}%`}
                  />
                  <MetricCardWithChange
                    title="Revenue"
                    icon={<DollarSign className="w-4 h-4 text-emerald-400" />}
                    loading={isLoadingGA}
                    value={formatCurrencyShort(gaData?.summary?.revenue || 0)}
                    change={gaData?.comparison?.revenueChange}
                  />
                  <MetricCardWithChange
                    title="Purchases"
                    icon={<Package className="w-4 h-4 text-amber-400" />}
                    loading={isLoadingGA}
                    value={formatNumber(gaData?.summary?.purchases || 0)}
                    change={gaData?.comparison?.purchasesChange}
                  />
                </div>

                {/* Funnel + Source Revenue */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-violet-400" />
                        <CardTitle className="text-violet-300 text-lg">Conversion Funnel</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {isLoadingGA ? (
                        <Skeleton className="h-64 w-full bg-slate-800" />
                      ) : funnelData.length > 0 ? (
                        <div className="space-y-4">
                          {funnelData.map((stage, index) => {
                            const maxValue = funnelData[0]?.value || 1;
                            const width = (stage.value / maxValue) * 100;
                            const dropOff = index > 0 
                              ? Math.round((1 - stage.value / funnelData[index - 1].value) * 100) 
                              : 0;
                            
                            return (
                              <div key={stage.name} className="space-y-1">
                                <div className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2">
                                    <span className="text-slate-300">{stage.name}</span>
                                    {dropOff > 0 && (
                                      <Badge variant="outline" className="text-xs border-red-700/50 text-red-400">
                                        -{dropOff}%
                                      </Badge>
                                    )}
                                  </div>
                                  <span className="text-slate-100 font-bold">{formatNumber(stage.value)}</span>
                                </div>
                                <div className="h-8 bg-slate-800 rounded-lg overflow-hidden relative">
                                  <div 
                                    className="h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-2"
                                    style={{ width: `${Math.max(width, 5)}%`, backgroundColor: stage.fill }}
                                  >
                                    {width > 20 && (
                                      <span className="text-white/90 text-xs font-medium">
                                        {formatPercent(width)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center py-12">No funnel data available</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-emerald-400" />
                        <CardTitle className="text-emerald-300 text-lg">Revenue by Source</CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {isLoadingGA ? (
                        <div className="space-y-2">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <Skeleton key={i} className="h-14 w-full bg-slate-800" />
                          ))}
                        </div>
                      ) : (gaData?.sourceRevenue?.length || 0) > 0 ? (
                        <div className="space-y-2 max-h-80 overflow-y-auto">
                          {gaData?.sourceRevenue.map((source, index) => (
                            <div
                              key={source.sourceMedium}
                              className="p-3 bg-slate-800/50 rounded-lg"
                            >
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <span className="text-emerald-400 font-semibold text-sm">#{index + 1}</span>
                                  <span className="text-slate-200 truncate text-sm">{source.sourceMedium}</span>
                                </div>
                                <span className="text-emerald-300 font-bold text-sm">
                                  {formatCurrency(source.revenue)}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 text-xs text-slate-400">
                                <span>{formatNumber(source.sessions)} sessions</span>
                                <span>{source.conversions} conv</span>
                                <span>{source.purchases} purch</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-slate-400 text-center py-8 text-sm">No revenue attribution data</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* User Type Comparison */}
                <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-violet-400" />
                      <CardTitle className="text-violet-300 text-lg">New vs Returning Visitor Value</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {isLoadingGA ? (
                      <Skeleton className="h-32 w-full bg-slate-800" />
                    ) : gaData?.userTypes ? (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-cyan-900/20 border border-cyan-700/30 rounded-lg">
                          <p className="text-cyan-300 text-sm font-medium mb-2">New Visitors</p>
                          <p className="text-2xl font-bold text-slate-100">
                            {formatNumber(gaData.userTypes.new.sessions)}
                          </p>
                          <div className="mt-2 space-y-1 text-xs text-slate-400">
                            <p>{gaData.userTypes.new.purchases} purchases</p>
                            <p className="text-emerald-300 font-medium">{formatCurrency(gaData.userTypes.new.revenue)}</p>
                          </div>
                        </div>
                        <div className="p-4 bg-emerald-900/20 border border-emerald-700/30 rounded-lg">
                          <p className="text-emerald-300 text-sm font-medium mb-2">Returning Visitors</p>
                          <p className="text-2xl font-bold text-slate-100">
                            {formatNumber(gaData.userTypes.returning.sessions)}
                          </p>
                          <div className="mt-2 space-y-1 text-xs text-slate-400">
                            <p>{gaData.userTypes.returning.purchases} purchases</p>
                            <p className="text-emerald-300 font-medium">{formatCurrency(gaData.userTypes.returning.revenue)}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-slate-400 text-center py-8">No user type data</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ==================== STORE TAB ==================== */}
              <TabsContent value="store" className="space-y-6">
                {!isPasswordAuth ? (
                  <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                    <CardContent className="p-8 text-center">
                      <Lock className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                      <p className="text-slate-400 mb-4">Store analytics requires password authentication</p>
                      <p className="text-slate-500 text-sm">Use the password login to access order data</p>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Export Buttons */}
                    {storeData && (
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-emerald-600/50 text-emerald-400 hover:bg-emerald-900/30"
                          onClick={() => exportFullStoreReport(storeData)}
                        >
                          <FileSpreadsheet className="w-4 h-4 mr-2" />
                          Export Store Report
                        </Button>
                      </div>
                    )}

                    {/* Store KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                      <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardDescription className="text-slate-400 text-xs uppercase tracking-wide">Today</CardDescription>
                            <DollarSign className="w-4 h-4 text-emerald-400" />
                          </div>
                        </CardHeader>
                        <CardContent>
                          {isLoadingStore || !storeMetrics ? (
                            <Skeleton className="h-8 w-20 bg-slate-800" />
                          ) : (
                            <p className="text-2xl font-bold text-slate-100">{formatCurrency(storeMetrics.revenue.today)}</p>
                          )}
                        </CardContent>
                      </Card>

                      <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardDescription className="text-slate-400 text-xs uppercase tracking-wide">7 Days</CardDescription>
                            <TrendingUp className="w-4 h-4 text-cyan-400" />
                          </div>
                        </CardHeader>
                        <CardContent>
                          {isLoadingStore || !storeMetrics ? (
                            <Skeleton className="h-8 w-20 bg-slate-800" />
                          ) : (
                            <p className="text-2xl font-bold text-slate-100">{formatCurrency(storeMetrics.revenue.sevenDays)}</p>
                          )}
                        </CardContent>
                      </Card>

                      <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardDescription className="text-slate-400 text-xs uppercase tracking-wide">30 Days</CardDescription>
                            <BarChart3 className="w-4 h-4 text-violet-400" />
                          </div>
                        </CardHeader>
                        <CardContent>
                          {isLoadingStore || !storeMetrics ? (
                            <Skeleton className="h-8 w-20 bg-slate-800" />
                          ) : (
                            <p className="text-2xl font-bold text-slate-100">{formatCurrency(storeMetrics.revenue.thirtyDays)}</p>
                          )}
                        </CardContent>
                      </Card>

                      <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardDescription className="text-slate-400 text-xs uppercase tracking-wide">AOV</CardDescription>
                            <ShoppingBag className="w-4 h-4 text-amber-400" />
                          </div>
                        </CardHeader>
                        <CardContent>
                          {isLoadingStore || !storeMetrics ? (
                            <Skeleton className="h-8 w-16 bg-slate-800" />
                          ) : (
                            <p className="text-2xl font-bold text-slate-100">{formatCurrency(storeMetrics.avgOrderValue)}</p>
                          )}
                        </CardContent>
                      </Card>

                      <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardDescription className="text-slate-400 text-xs uppercase tracking-wide">Fulfilment</CardDescription>
                            <Clock className="w-4 h-4 text-amber-400" />
                          </div>
                        </CardHeader>
                        <CardContent>
                          {isLoadingStore || !storeMetrics ? (
                            <Skeleton className="h-8 w-16 bg-slate-800" />
                          ) : (
                            <>
                              <p className="text-2xl font-bold text-slate-100">{formatHours(storeMetrics.avgFulfilmentHours)}</p>
                              <p className="text-xs text-slate-400">avg time</p>
                            </>
                          )}
                        </CardContent>
                      </Card>

                      <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardDescription className="text-slate-400 text-xs uppercase tracking-wide">Unfulfilled</CardDescription>
                            <AlertCircle className="w-4 h-4 text-rose-400" />
                          </div>
                        </CardHeader>
                        <CardContent>
                          {isLoadingStore || !storeMetrics ? (
                            <Skeleton className="h-8 w-12 bg-slate-800" />
                          ) : (
                            <p className={`text-2xl font-bold ${storeMetrics.unfulfilledCount > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                              {storeMetrics.unfulfilledCount}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Store Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                        <CardHeader>
                          <CardTitle className="text-emerald-300 text-lg">Revenue (30 Days)</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {isLoadingStore ? (
                            <Skeleton className="h-64 w-full bg-slate-800" />
                          ) : storeChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                              <AreaChart data={storeChartData}>
                                <defs>
                                  <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis 
                                  dataKey="date" 
                                  stroke="#cbd5e1" 
                                  tick={{ fontSize: 10 }}
                                  tickFormatter={(value) => formatShortDate(value)}
                                  interval="preserveStartEnd"
                                />
                                <YAxis 
                                  stroke="#cbd5e1" 
                                  tick={{ fontSize: 10 }}
                                  tickFormatter={(value) => `£${value}`}
                                />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: "#1e293b",
                                    border: "1px solid #334155",
                                    borderRadius: "8px",
                                    color: "#f1f5f9",
                                  }}
                                  formatter={(value: number) => [formatCurrency(value), 'Revenue']}
                                  labelFormatter={(label) => formatUKDate(label)}
                                />
                                <Area 
                                  type="monotone" 
                                  dataKey="revenue" 
                                  stroke="#10b981" 
                                  strokeWidth={2}
                                  fill="url(#revenueGradient)" 
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          ) : (
                            <p className="text-slate-400 text-center py-12">No revenue data yet</p>
                          )}
                        </CardContent>
                      </Card>

                      <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                        <CardHeader>
                          <CardTitle className="text-cyan-300 text-lg">Orders (30 Days)</CardTitle>
                        </CardHeader>
                        <CardContent>
                          {isLoadingStore ? (
                            <Skeleton className="h-64 w-full bg-slate-800" />
                          ) : storeChartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                              <BarChart data={storeChartData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                <XAxis 
                                  dataKey="date" 
                                  stroke="#cbd5e1" 
                                  tick={{ fontSize: 10 }}
                                  tickFormatter={(value) => formatShortDate(value)}
                                  interval="preserveStartEnd"
                                />
                                <YAxis stroke="#cbd5e1" tick={{ fontSize: 10 }} />
                                <Tooltip
                                  contentStyle={{
                                    backgroundColor: "#1e293b",
                                    border: "1px solid #334155",
                                    borderRadius: "8px",
                                    color: "#f1f5f9",
                                  }}
                                  formatter={(value: number) => [value, 'Orders']}
                                  labelFormatter={(label) => formatUKDate(label)}
                                />
                                <Bar dataKey="orders" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <p className="text-slate-400 text-center py-12">No order data yet</p>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Store Tables */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                        <CardHeader>
                          <div className="flex items-center gap-2">
                            <Package className="w-5 h-5 text-violet-400" />
                            <CardTitle className="text-violet-300 text-lg">Best Sellers</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {isLoadingStore ? (
                            <div className="space-y-2">
                              {[1, 2, 3, 4, 5].map((i) => (
                                <Skeleton key={i} className="h-12 w-full bg-slate-800" />
                              ))}
                            </div>
                          ) : topProducts.length > 0 ? (
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                              {topProducts.map((product, index) => (
                                <div
                                  key={product.name}
                                  className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <span className="text-violet-400 font-semibold text-sm w-5">#{index + 1}</span>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-slate-200 text-sm truncate">{product.name}</p>
                                      <p className="text-slate-500 text-xs">{product.units} units</p>
                                    </div>
                                  </div>
                                  <span className="text-slate-100 font-bold text-sm">{formatCurrency(product.revenue)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-400 text-center py-8">No product data</p>
                          )}
                        </CardContent>
                      </Card>

                      <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                        <CardHeader>
                          <div className="flex items-center gap-2">
                            <Users className="w-5 h-5 text-amber-400" />
                            <CardTitle className="text-amber-300 text-lg">Top Customers</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {isLoadingStore ? (
                            <div className="space-y-2">
                              {[1, 2, 3, 4, 5].map((i) => (
                                <Skeleton key={i} className="h-12 w-full bg-slate-800" />
                              ))}
                            </div>
                          ) : topCustomers.length > 0 ? (
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                              {topCustomers.map((customer, index) => (
                                <div
                                  key={customer.email}
                                  className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <span className="text-amber-400 font-semibold text-sm w-5">#{index + 1}</span>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-slate-200 text-sm truncate">{customer.name}</p>
                                      <p className="text-slate-500 text-xs">{customer.orders} orders</p>
                                    </div>
                                  </div>
                                  <span className="text-slate-100 font-bold text-sm">{formatCurrency(customer.spend)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-400 text-center py-8">No customer data</p>
                          )}
                        </CardContent>
                      </Card>

                      <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
                        <CardHeader>
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-emerald-400" />
                            <CardTitle className="text-emerald-300 text-lg">Recent Orders</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {isLoadingStore ? (
                            <div className="space-y-2">
                              {[1, 2, 3, 4, 5].map((i) => (
                                <Skeleton key={i} className="h-12 w-full bg-slate-800" />
                              ))}
                            </div>
                          ) : recentTrends.length > 0 ? (
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                              {recentTrends.map((order) => (
                                <div
                                  key={order.id}
                                  className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className={`w-2 h-2 rounded-full ${order.fulfilled ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                                    <div className="min-w-0 flex-1">
                                      <p className="text-slate-200 text-sm truncate">{order.customer}</p>
                                      <p className="text-slate-500 text-xs">{formatShortDate(order.date)}</p>
                                    </div>
                                  </div>
                                  <span className="text-slate-100 font-bold text-sm">{formatCurrency(order.total)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-400 text-center py-8">No recent orders</p>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>
          </section>
        </Page>
      </div>
    </>
  );
}

// ==================== HELPER COMPONENTS ====================

function ExecutiveCard({
  title,
  value,
  change,
  icon,
  loading,
  color,
  invertColor = false,
}: {
  title: string;
  value: string;
  change?: number;
  icon: React.ReactNode;
  loading: boolean;
  color: "emerald" | "cyan" | "violet" | "amber" | "rose";
  invertColor?: boolean;
}) {
  const colorClasses = {
    emerald: "text-emerald-400",
    cyan: "text-cyan-400",
    violet: "text-violet-400",
    amber: "text-amber-400",
    rose: "text-rose-400",
  };

  const isPositive = (change || 0) >= 0;
  const changeColor = invertColor 
    ? (isPositive ? "text-red-400" : "text-emerald-400")
    : (isPositive ? "text-emerald-400" : "text-red-400");

  return (
    <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-slate-400 uppercase tracking-wide">{title}</span>
          <span className={colorClasses[color]}>{icon}</span>
        </div>
        {loading ? (
          <Skeleton className="h-6 w-16 bg-slate-800" />
        ) : (
          <>
            <p className="text-lg font-bold text-slate-100">{value}</p>
            {change !== undefined && change !== 0 && (
              <div className={`flex items-center gap-0.5 text-[10px] ${changeColor}`}>
                {isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                <span>{Math.abs(change).toFixed(1)}%</span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MetricCard({ 
  title, 
  icon, 
  loading, 
  value, 
  subtitle 
}: { 
  title: string; 
  icon: React.ReactNode; 
  loading: boolean; 
  value: string; 
  subtitle?: string;
}) {
  return (
    <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardDescription className="text-slate-400 text-xs uppercase tracking-wide">{title}</CardDescription>
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20 bg-slate-800" />
        ) : (
          <>
            <p className="text-2xl font-bold text-slate-100">{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MetricCardWithChange({ 
  title, 
  icon, 
  loading, 
  value, 
  change 
}: { 
  title: string; 
  icon: React.ReactNode; 
  loading: boolean; 
  value: string; 
  change?: number;
}) {
  const isPositive = (change || 0) >= 0;
  const changeColor = isPositive ? "text-emerald-400" : "text-red-400";
  const ChangeIcon = isPositive ? ArrowUpRight : ArrowDownRight;

  return (
    <Card className="bg-slate-900/70 border-slate-700/60 backdrop-blur-md">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardDescription className="text-slate-400 text-xs uppercase tracking-wide">{title}</CardDescription>
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20 bg-slate-800" />
        ) : (
          <>
            <p className="text-2xl font-bold text-slate-100">{value}</p>
            {change !== undefined && change !== 0 && (
              <div className={`flex items-center gap-1 text-xs mt-1 ${changeColor}`}>
                <ChangeIcon className="w-3 h-3" />
                <span>{Math.abs(change).toFixed(1)}%</span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
